import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Card, CardBody, VStack, HStack, Text, Switch, Divider,
  Container, Select, FormControl, FormLabel, Button, useToast, useColorMode,
} from '@chakra-ui/react';
import { FiBell, FiMoon, FiGlobe, FiSave } from 'react-icons/fi';

const PREFS_KEY = 'toviAdminPreferences';

const defaultPrefs = {
  emailNotifications: true,
  soundNotifications: true,
  autoRefreshBadges: true,
  language: 'pt-BR',
};

const PreferencesPage = () => {
  const toast = useToast();
  const { colorMode, toggleColorMode } = useColorMode();
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [saved, setSaved] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(PREFS_KEY);
    if (stored) {
      try {
        setPrefs({ ...defaultPrefs, ...JSON.parse(stored) });
      } catch {
        // Ignore corrupt data
      }
    }
  }, []);

  const updatePref = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    setSaved(true);
    toast({ title: 'Preferências salvas', status: 'success', duration: 2000, isClosable: true, position: 'top-right' });
  };

  return (
    <Container maxW="container.md" py={8}>
      <Heading size="lg" mb={6}>Preferências</Heading>

      {/* Notifications */}
      <Card mb={6}>
        <CardBody>
          <HStack mb={4}>
            <Box as={FiBell} color="gray.500" />
            <Heading size="md">Notificações</Heading>
          </HStack>

          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">Notificações por e-mail</Text>
                <Text fontSize="sm" color="gray.500">Receber alertas de novos tickets por e-mail</Text>
              </VStack>
              <Switch
                colorScheme="teal"
                isChecked={prefs.emailNotifications}
                onChange={(e) => updatePref('emailNotifications', e.target.checked)}
              />
            </HStack>

            <Divider />

            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">Sons de notificação</Text>
                <Text fontSize="sm" color="gray.500">Reproduzir som ao receber notificações</Text>
              </VStack>
              <Switch
                colorScheme="teal"
                isChecked={prefs.soundNotifications}
                onChange={(e) => updatePref('soundNotifications', e.target.checked)}
              />
            </HStack>

            <Divider />

            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">Atualização automática de badges</Text>
                <Text fontSize="sm" color="gray.500">Atualizar contadores do menu a cada 5 minutos</Text>
              </VStack>
              <Switch
                colorScheme="teal"
                isChecked={prefs.autoRefreshBadges}
                onChange={(e) => updatePref('autoRefreshBadges', e.target.checked)}
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Appearance */}
      <Card mb={6}>
        <CardBody>
          <HStack mb={4}>
            <Box as={FiMoon} color="gray.500" />
            <Heading size="md">Aparência</Heading>
          </HStack>

          <HStack justify="space-between">
            <VStack align="start" spacing={0}>
              <Text fontWeight="medium">Modo escuro</Text>
              <Text fontSize="sm" color="gray.500">Alternar entre tema claro e escuro</Text>
            </VStack>
            <Switch
              colorScheme="teal"
              isChecked={colorMode === 'dark'}
              onChange={toggleColorMode}
            />
          </HStack>
        </CardBody>
      </Card>

      {/* Language */}
      <Card mb={6}>
        <CardBody>
          <HStack mb={4}>
            <Box as={FiGlobe} color="gray.500" />
            <Heading size="md">Idioma</Heading>
          </HStack>

          <FormControl maxW="300px">
            <FormLabel>Idioma do painel</FormLabel>
            <Select
              value={prefs.language}
              onChange={(e) => updatePref('language', e.target.value)}
            >
              <option value="pt-BR">Português (Brasil)</option>
              <option value="en" disabled>English (em breve)</option>
              <option value="es" disabled>Español (em breve)</option>
            </Select>
          </FormControl>
        </CardBody>
      </Card>

      {/* Save */}
      <Button
        colorScheme="teal"
        leftIcon={<FiSave />}
        onClick={handleSave}
        isDisabled={saved}
        size="lg"
      >
        {saved ? 'Preferências salvas' : 'Salvar Preferências'}
      </Button>
    </Container>
  );
};

export default PreferencesPage;
